import { useCallback, useEffect, useRef, useState } from "react";
import PaymentService from "@/services/NPS/Stepper/APIs/PaymentService";
import { Button } from "@/projects/NPS/Stepper/common/button";
import { useNavigate } from "react-router-dom";
import navigationRoutes from "@/utils/navigationRoutes";
import getRegistrationDetailsService from "@/services/NPS/Stepper/APIs/getRegistrationDetailsService";

const PaymentStatusModal = ({
  ackId,
  paypointzPaymentId,
  amount,
  setPaypointzPaymentId,
  onClose,
}: {
  ackId: string;
  paypointzPaymentId?: string | null;
  amount: number;
  setPaypointzPaymentId: (paypointzPaymentId: string) => void;
  summary?: {
    pran_total_contribution: number;
    pran_ac_opening_charges: number;
    pran_ac_opening_charges_gst: number;
    total_payable_amount: number;
  };
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"pending" | "success" | "failed">(
    "pending"
  );
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown
  const [redirectTimer, setRedirectTimer] = useState(5); // 5-sec redirect timer
  const [isFetchingPran, setIsFetchingPran] = useState(false);
  const pranRef = useRef<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pranPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pranWaitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pranPollCountRef = useRef<number>(0);

  useEffect(() => {
    const checkStatus = async () => {
      const res = await PaymentService.getPaymentStatus(
        paypointzPaymentId ?? ""
      );
      const status = res?.status?.toLowerCase();

      if (status === "success" || status === "failed") {
        setStatus(status);
      }
    };

    checkStatus();
  }, []);

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await PaymentService.getPaymentStatus(
        paypointzPaymentId ?? ""
      );

      if (res?.status) {
        const newStatus = res.status as "pending" | "success" | "failed";
        setStatus(newStatus);

        if (newStatus === "success" || newStatus === "failed") {
          if (pollIntervalRef.current) {
            setPaypointzPaymentId(paypointzPaymentId ?? "");
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error("Status fetch error", error);
    }
  }, [paypointzPaymentId, setPaypointzPaymentId]);

  const startPolling = useCallback(() => {
    pollIntervalRef.current ??= setInterval(fetchStatus, 5000);
  }, [fetchStatus]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // -------------------------------
  // Start timer + initial poll
  // -------------------------------
  useEffect(() => {
    if (status === "pending") {
      fetchStatus();
      startPolling();
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      stopPolling();
    };
  }, [fetchStatus, startPolling, stopPolling, status]);

  // Function to fetch track status and check for PRAN
  const fetchTrackStatus = useCallback(async (): Promise<boolean> => {
    try {
      // Get ackId from registration details if not provided
      let ackIdToUse = ackId;
      if (!ackIdToUse) {
        const registrationResponse =
          await getRegistrationDetailsService.getRegistrationDetails();
        if (registrationResponse.success) {
          ackIdToUse =
            registrationResponse.registration?.initialDetails?.ackId ?? "";
        }
      }

      if (ackIdToUse) {
        const trackStatusResponse = await PaymentService.trackStatus({
          ackId: ackIdToUse,
        });
        if (
          trackStatusResponse?.success &&
          trackStatusResponse?.trackStatus?.pran &&
          trackStatusResponse.trackStatus.pran !== null
        ) {
          pranRef.current = trackStatusResponse.trackStatus.pran;
          return true; // PRAN found
        }
      }
      return false; // PRAN not found yet
    } catch (error) {
      console.error("Error fetching track status:", error);
      return false;
    }
  }, [ackId]);

  useEffect(() => {
    if (status === "success") {
      setIsFetchingPran(true);
      pranPollCountRef.current = 0; // Reset poll count
      const maxPolls = 60; // Maximum 60 polls (5 minutes total after 10 sec wait)

      // Wait 10 seconds before starting to poll for PRAN
      pranWaitTimeoutRef.current = setTimeout(() => {
        // Start polling for PRAN every 5 seconds
        const pollForPran = async () => {
          pranPollCountRef.current++;

          // If we've polled too many times, navigate anyway (PRAN might be null)
          if (pranPollCountRef.current > maxPolls) {
            setIsFetchingPran(false);
            if (pranPollIntervalRef.current) {
              clearInterval(pranPollIntervalRef.current);
              pranPollIntervalRef.current = null;
            }
            // Navigate even if PRAN is not found (after max attempts)
            navigate(navigationRoutes.PRAN_SUCCESS_SCREEN, {
              state: pranRef.current ? { pran: pranRef.current } : undefined,
            });
            return;
          }

          const pranFound = await fetchTrackStatus();
          if (pranFound) {
            // PRAN found, stop polling and navigate
            setIsFetchingPran(false);
            if (pranPollIntervalRef.current) {
              clearInterval(pranPollIntervalRef.current);
              pranPollIntervalRef.current = null;
            }
            // Navigate immediately when PRAN is found
            navigate(navigationRoutes.PRAN_SUCCESS_SCREEN, {
              state: { pran: pranRef.current },
            });
          }
        };

        // Call immediately first time
        pollForPran();

        // Then poll every 5 seconds
        pranPollIntervalRef.current = setInterval(pollForPran, 5000);
      }, 10000); // Wait 10 seconds

      return () => {
        if (pranWaitTimeoutRef.current) {
          clearTimeout(pranWaitTimeoutRef.current);
        }
        if (pranPollIntervalRef.current) {
          clearInterval(pranPollIntervalRef.current);
        }
      };
    }
  }, [status, ackId, fetchTrackStatus, navigate]);

  useEffect(() => {
    if (status === "failed") {
      const interval = setInterval(() => {
        setRedirectTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            navigate(navigationRoutes.PAYMENT_FAILED_SCREEN);
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
    // For success, navigation is handled in the PRAN polling effect above
  }, [status, navigate]);

  // -----------------------------------------------------
  //         UI Rendering (SUCCESS / FAILED / PENDING)
  // -----------------------------------------------------

  // 🎉 SUCCESS UI (Screenshot 1)
  if (status === "success") {
    return (
      <div className="mx-auto w-full px-10 py-12 text-center font-nunito sm:w-[500px]">
        <div className="text-center text-[14px] font-semibold text-[#1DAB91]">
          {isFetchingPran
            ? "Fetching your PRAN. Please wait..."
            : pranRef.current
              ? "Redirecting to success page..."
              : "Redirecting in a moment..."}
        </div>
        <div className="mx-auto mt-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#E4FFFB]">
          <img
            src="/assets/Checkmark Circle.svg"
            alt="success"
            className="h-[60px]"
          />
        </div>

        <h2 className="mt-4 text-center font-nunito text-[25px] font-extrabold leading-[50px] text-[var(--Dark-color,#2F363F)] md:text-[40px]">
          Congratulations
        </h2>

        <p className="mt-2 text-center font-nunito text-[16px] font-bold leading-[26px] text-[var(--Secondary,#707070)]">
          Payment Done
        </p>
        <p className="mt-2 text-center font-nunito text-[16px] font-bold leading-[26px] text-[var(--Secondary,#707070)]">
          Transaction Completed
        </p>

        {pranRef.current && (
          <div className="mt-4 rounded-lg bg-[#F5F5F5] p-3">
            <p className="mb-1 font-nunito text-sm font-semibold text-[#707070]">
              Your PRAN:
            </p>
            <p className="font-nunito text-lg font-bold text-[#228B22]">
              {pranRef.current}
            </p>
          </div>
        )}

        <p className="mt-4 text-center font-nunito text-[16px] font-bold leading-[26px] text-[var(--Dark-color,#2F363F)]">
          Ref: {paypointzPaymentId}
        </p>

        <Button
          onClick={onClose}
          variant="secondary"
          className="m-auto mt-6 h-[50px] cursor-pointer rounded-[8px] bg-[var(--Green,#F5F5F5)] py-2 font-nunito text-[18px] font-semibold text-[#707070]"
        >
          Close
        </Button>
      </div>
    );
  }

  // ❌ FAILED UI (Screenshot 2)
  if (status === "failed") {
    return (
      <div className="w-f px-10 py-12 text-center font-nunito sm:w-[500px]">
        <div className="text-center text-[14px] font-semibold text-red-600">
          Redirecting in {redirectTimer} seconds...
        </div>
        <div className="mx-auto mt-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#FFDEDE]">
          <img src="/assets/error.svg" alt="failed" className="h-[60px]" />
        </div>

        <h2 className="mt-6 font-nunito text-[32px] font-bold text-[#2F363F]">
          Oops!
        </h2>

        <p className="mt-2 font-nunito text-[18px] font-semibold text-[#707070]">
          Payment Failed
        </p>
        <p className="mt-2 font-nunito text-[18px] font-semibold text-[#707070]">
          Transaction Failed
        </p>

        <p className="mt-4 font-nunito text-[18px] font-bold text-[#2F363F]">
          Ref: {paypointzPaymentId}
        </p>

        <Button
          onClick={onClose}
          className="mx-auto mt-8 h-[50px] rounded-md bg-[#F5F5F5] px-8 font-nunito text-[18px] font-semibold text-[#707070]"
        >
          Close
        </Button>
      </div>
    );
  }

  // ⏳ PENDING UI
  return (
    <div className="px-10 py-12 text-center font-nunito">
      <div className="mx-auto mt-6 flex h-[90px] w-[90px] items-center justify-center rounded-full bg-[#FFF7E6]">
        <img
          src="/assets/clock.png"
          alt="clock"
          className="h-[45px] w-[45px]"
        />
      </div>

      <div className="mt-6 font-nunito text-[26px] font-bold text-[#266985]">
        ₹{amount}
      </div>

      <p className="mt-2 font-nunito text-[16px] font-semibold text-[#707070]">
        Click on the notification received on your UPI App & enter your UPI PIN
      </p>

      <div className="mt-4 font-nunito text-[25px] font-bold text-[#2F363F]">
        {formatTime(timeLeft)}
      </div>

      <p className="mt-3 font-nunito text-[16px] font-bold text-[#707070]">
        To check payment status, click{" "}
        <span
          onClick={fetchStatus}
          className="cursor-pointer font-nunito text-[16px] font-bold text-[#1DAB91] underline"
        >
          {"Refresh"}
        </span>
      </p>

      <p className="mt-3 max-w-xl font-nunito text-[16px] font-bold text-[#707070]">
        NOTE: You will be redirected to another page once your payment is
        processed. Do not close this page or press back.
      </p>
    </div>
  );
};

export default PaymentStatusModal;
